import { onSnapshot, type Unsubscribe } from "firebase/firestore";
import { createGlobalState } from "@vueuse/core";
import { useFirestore } from "~/composables/useFirestore";

type FeatureBlockData = {
  pos: boolean;
  setting: boolean;
  report: boolean;
  expense: boolean;
  download: boolean;
  manager: boolean;
};

export const useWatchFeatureBlock = createGlobalState(() => {
  const defaultFeatureBlockData: FeatureBlockData = {
    pos: false,
    setting: false,
    report: false,
    expense: false,
    download: false,
    manager: false,
  };

  const normalizeFeatureBlockData = (payload: any): FeatureBlockData => {
    return {
      pos: payload?.pos === true,
      setting: payload?.setting === true,
      report: payload?.report === true,
      expense: payload?.expense === true,
      download: payload?.download === true,
      manager: payload?.manager === true,
    };
  };

  const featureBlockData = ref<FeatureBlockData>({ ...defaultFeatureBlockData });
  const isWatching = ref(false);
  let unsubscribe: Unsubscribe | null = null;

  const getRouteBlockKey = (path: string): keyof FeatureBlockData | null => {
    const normalizedPath = path.replace(/\/+$/, "") || "/";

    if (normalizedPath === "/pos" || normalizedPath === "/v2/pos") return "pos";
    if (normalizedPath.startsWith("/v2/settings") || normalizedPath.startsWith("/settings") || normalizedPath.startsWith("/setting")) return "setting";
    if (normalizedPath.startsWith("/v2/reports") || normalizedPath.startsWith("/reports") || normalizedPath.startsWith("/report")) return "report";
    if (normalizedPath.startsWith("/v2/expense") || normalizedPath.startsWith("/expenses") || normalizedPath.startsWith("/expense")) return "expense";
    if (normalizedPath.startsWith("/download")) return "download";
    if (normalizedPath.startsWith("/v2/menu") || normalizedPath.startsWith("/v2/edit-bill") || normalizedPath.startsWith("/manager")) return "manager";

    return null;
  };

  const isPathBlocked = (path: string) => {
    const key = getRouteBlockKey(path);
    if (!key) return false;
    return featureBlockData.value[key] === true;
  };

  const startWatching = () => {
    if (isWatching.value) return;

    const refDoc = useFirestore().getFeatureBlockRef();
    unsubscribe = onSnapshot(
      refDoc,
      (snapshot) => {
        if (!snapshot.exists()) {
          console.log("[useWatchFeatureBlock] feature_block not found");
          featureBlockData.value = { ...defaultFeatureBlockData };
          return;
        }

        featureBlockData.value = normalizeFeatureBlockData(snapshot.data());
        console.log("[useWatchFeatureBlock] feature_block:", featureBlockData.value);
      },
      (error) => {
        console.log("[useWatchFeatureBlock] snapshot error:", error);
        featureBlockData.value = { ...defaultFeatureBlockData };
      }
    );

    isWatching.value = true;
  };

  const stopWatching = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    isWatching.value = false;
  };

  return {
    featureBlockData,
    isWatching,
    startWatching,
    stopWatching,
    isPathBlocked,
  };
});
